import Section from '@pelckmans/elementis/components/section';
import { Cell } from '@pelckmans/elementis/components/table';
import { useTranslation } from 'react-i18next';

import useAssignmentsStudent from '../hooks/useAssignmentsStudent';
import { StudentAssignment } from '../api/AssignmentStudentApi';
import AssignmentDivision from './AssignmentDivision';
import { splitAssignmentsByEndDateCategory } from '../../utils';
import LoadingState from '../../../../shared-components/LoadingState';
import EmptyState from '../../../../shared-components/EmptyState';

const BASE = 'PAGES.ASSIGNMENTS.STUDENT.SECTION';

function AssignmentsBody({ assignments }: { assignments: StudentAssignment[] }) {
  const { assignmentsEndIn24hours, assignmentsEndIn7Days, assignmentsEndOver7Days } =
    splitAssignmentsByEndDateCategory(assignments);

  return (
    <>
      <AssignmentDivision endDateCategory="TODAY" assigments={assignmentsEndIn24hours} />
      <AssignmentDivision endDateCategory="THIS_WEEK" assigments={assignmentsEndIn7Days} />
      <AssignmentDivision endDateCategory="LATER" assigments={assignmentsEndOver7Days} />
    </>
  );
}

type AssignmentsTableProps = {
  assignments: StudentAssignment[];
};

function AssignmentsTable({ assignments }: AssignmentsTableProps) {
  const { t } = useTranslation();

  return (
    <table className="assignments-table" data-testid="assignments-table">
      <thead>
        <tr>
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.MATERIAL`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.SECTION`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.PERIOD`)} />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.RESULT`)} className="results-header-cell" />
          <Cell.Header title={t(`${BASE}.TABLE.HEADER.ATTEMPTS`)} />
        </tr>
      </thead>
      <tbody>
        <AssignmentsBody assignments={assignments} />
      </tbody>
    </table>
  );
}

function Assignments() {
  const { t } = useTranslation();
  const { assignments, isLoading } = useAssignmentsStudent();

  let content;

  if (isLoading) {
    content = <LoadingState variant="table" aria-label={t(`${BASE}.TABLE.EMPTY_STATE.A11Y_LABEL`)} />;
  } else if (assignments.length === 0) {
    content = (
      <EmptyState
        variant="table"
        headerTitle={t(`${BASE}.TABLE.EMPTY_STATE.TITLE`)}
        bodyText={t(`${BASE}.TABLE.EMPTY_STATE.BODY`)}
      />
    );
  } else {
    content = <AssignmentsTable assignments={assignments} />;
  }

  return <Section title={t(`${BASE}.TITLE`)}>{content}</Section>;
}

export default Assignments;
